import type { components } from "@brandex/api-client";
import { useBrandex } from "@brandex/dashboard";
import { Alert, Badge, Button, Field, Input, Select, Spinner, Textarea, cn } from "@brandex/ui";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarPlus, ExternalLink, FileBarChart, GripVertical, Pencil, Search, Send } from "lucide-react";
import { useState } from "react";

import { useWebsites } from "../data";
import { QueryError } from "../views";
import { PublishForm } from "./publish";

type Deliverable = components["schemas"]["DeliverableDto"];
type Status = Deliverable["status"];

/** Board columns, in pipeline order (prototype), each with its marker colour. */
export const COLUMNS: readonly { status: Status; label: string; dot: string }[] = [
  { status: "planned", label: "Planned", dot: "bg-slate-400" },
  { status: "shot", label: "Shot", dot: "bg-sky-400" },
  { status: "editing", label: "Editing", dot: "bg-indigo-400" },
  { status: "internal_review", label: "Internal review", dot: "bg-violet-400" },
  { status: "sent_to_doctor", label: "Sent to doctor", dot: "bg-amber-400" },
  { status: "changes_requested", label: "Changes requested", dot: "bg-coral-400" },
  { status: "approved", label: "Approved", dot: "bg-emerald-400" },
  { status: "published", label: "Published", dot: "bg-teal-400" },
];
const LABEL = Object.fromEntries(COLUMNS.map((c) => [c.status, c.label])) as Record<Status, string>;
const KIND: Record<Deliverable["kind"], string> = {
  podcast: "Podcast", reel: "Reel", photo_card: "Photo card", promo_shoot: "Promo shoot",
  article: "Article", website_section: "Website section", other: "Other",
};

/** The current month in Dhaka, `YYYY-MM`. */
export function currentPeriod(now = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Dhaka", year: "numeric", month: "2-digit" }).format(now).slice(0, 7);
}

/**
 * Where the team may move an item: the server's next steps, except that an
 * item with the doctor only goes back by "recall" (to internal review).
 * Approval is the doctor's alone, so it never appears here.
 */
export function movesFor(item: Pick<Deliverable, "status" | "next">): Status[] {
  return item.next.filter((to) => to !== "approved");
}

/**
 * Where a card can go on the board: the team's moves, plus Published for an
 * approved item (that "move" is logging where it went live, which needs the
 * post link, so it opens the publish form rather than calling move).
 */
export function boardMoves(item: Pick<Deliverable, "status" | "next">): Status[] {
  return item.status === "approved" ? ["published"] : movesFor(item);
}

/** A card's open panel: edit (optionally focused on the preview link) or publish. */
type Panel = { id: string; kind: "edit" | "preview" | "publish" };

/**
 * The doctor reviews the cut at its preview link, so the server refuses to
 * send an item without one. The board knows which items lack it and asks
 * for the link instead of trying (and failing).
 */
export function needsPreview(item: Pick<Deliverable, "preview_url">, to: Status): boolean {
  return to === "sent_to_doctor" && !item.preview_url;
}

/** The client in the address (`/pipeline?client=…`), so a board can be bookmarked or shared. */
function clientFromUrl(): string {
  return new URLSearchParams(window.location.search).get("client") ?? "";
}

function rememberClient(org: string) {
  const url = new URL(window.location.href);
  if (org === "") url.searchParams.delete("client");
  else url.searchParams.set("client", org);
  window.history.replaceState(window.history.state, "", url);
}

/** Two letters for a doctor chip ("Dr. Demo One" → "DO"). */
export function initials(name: string): string {
  const words = name.replace(/^(dr|prof)\.?\s+/i, "").split(/\s+/).filter(Boolean);
  return ((words[0]?.[0] ?? "") + (words[1]?.[0] ?? "")).toUpperCase() || "?";
}

/** How often the board checks for the doctors' approvals and requests. */
const REFRESH_MS = 30_000;

const pipelineKey = (org: string, period: string) => ["admin", "pipeline", org, period] as const;

/**
 * Production pipeline (F-09): a Trello-style board over all clients or one,
 * for a month. Drag a card (by its handle) onto a column it may move to;
 * only the columns allowed for that item light up. Each column scrolls on
 * its own and the board scrolls sideways, so the page never overflows; on
 * phones the board snaps column by column and a long press starts a drag.
 * Approval is set by the doctor only, so items sent to the doctor wait.
 */
export function PipelinePage() {
  const { api } = useBrandex();
  const qc = useQueryClient();
  const clients = useWebsites();
  const [org, setOrgState] = useState(clientFromUrl);
  const [search, setSearch] = useState("");
  const setOrg = (value: string) => {
    setOrgState(value);
    rememberClient(value);
  };
  const [period, setPeriod] = useState(currentPeriod);
  const [dragging, setDragging] = useState<Deliverable | null>(null);
  const [notice, setNotice] = useState<{ tone: "info" | "success"; text: string } | null>(null);
  /** The card whose edit or publish form is open. */
  const [panel, setPanel] = useState<Panel | null>(null);
  const key = pipelineKey(org, period);
  const items = useQuery({
    queryKey: key,
    refetchInterval: REFRESH_MS,
    queryFn: async () => {
      const { data } = await api.http.GET("/v1/admin/deliverables", {
        params: { query: { period, ...(org === "" ? {} : { org_id: org }) } },
      });
      if (data === undefined) throw new Error("Empty response");
      return data;
    },
  });
  const plan = useMutation({
    mutationFn: async () => {
      const { data } = await api.http.POST("/v1/admin/organizations/{org_id}/plan", { params: { path: { org_id: org } }, body: { period } });
      return data ?? [];
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["admin", "pipeline"] }),
  });
  const move = useMutation({
    mutationFn: async ({ item, to }: { item: Deliverable; to: Status }) => {
      await api.http.POST("/v1/admin/deliverables/{deliverable_id}/move", { params: { path: { deliverable_id: item.id } }, body: { to } });
    },
    // Show the move at once; put the card back if the server refuses.
    onMutate: async ({ item, to }) => {
      setNotice(null);
      await qc.cancelQueries({ queryKey: key });
      const before = qc.getQueryData<Deliverable[]>(key);
      qc.setQueryData<Deliverable[]>(key, (list) => list?.map((d) => (d.id === item.id ? { ...d, status: to, next: [] } : d)));
      return { before };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.before) qc.setQueryData(key, ctx.before);
    },
    onSuccess: (_d, { item, to }) => {
      if (to === "sent_to_doctor") {
        setNotice({ tone: "success", text: `Sent “${item.title}” to ${item.doctor_name ?? "the doctor"}. It's now in their Content and approvals, and they've been notified.` });
      }
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: ["admin", "pipeline"] }),
  });
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    // A long press on phones, so swiping still scrolls the board.
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } }),
    useSensor(KeyboardSensor),
  );
  const loaded = items.data ?? [];
  const needle = search.trim().toLowerCase();
  const all = needle === "" ? loaded : loaded.filter((d) => `${d.title} ${d.doctor_name ?? ""}`.toLowerCase().includes(needle));
  const client = (clients.data ?? []).find((c) => c.organization_id === org);
  const onDragStart = (e: DragStartEvent) => setDragging(all.find((d) => d.id === e.active.id) ?? null);
  /** Moves a card, or asks for what the move still needs. */
  const tryMove = (item: Deliverable, to: Status) => {
    if (needsPreview(item, to)) {
      setNotice({ tone: "info", text: `Add a preview link to “${item.title}” before sending it to the doctor: they review the cut there.` });
      setPanel({ id: item.id, kind: "preview" });
      return;
    }
    if (to === "published") {
      setNotice({ tone: "info", text: `Where did “${item.title}” go live? Add the post link to mark it published.` });
      setPanel({ id: item.id, kind: "publish" });
      return;
    }
    move.mutate({ item, to });
  };
  const onDragEnd = (e: DragEndEvent) => {
    setDragging(null);
    const item = all.find((d) => d.id === e.active.id);
    const to = e.over?.id as Status | undefined;
    if (item === undefined || to === undefined || to === item.status) return;
    if (!boardMoves(item).includes(to)) {
      setNotice({ tone: "info", text: to === "approved" ? "Only the doctor can approve an item." : `“${item.title}” can't move from ${LABEL[item.status]} to ${LABEL[to]}.` });
      return;
    }
    tryMove(item, to);
  };
  const allowed = dragging === null ? null : boardMoves(dragging);
  return (
    <div className="grid min-w-0 gap-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Production pipeline</h1>
          <p className="mt-1 text-sm text-muted-foreground">Drag a card by its handle to move it. Approval is set by the doctor only.</p>
        </div>
        <div className="flex w-full flex-wrap items-end gap-3 sm:w-auto">
          <Field label="Client" className="min-w-0 flex-1 sm:flex-none">
            <Select className="w-full sm:min-w-48" value={org} onChange={(e) => setOrg(e.target.value)}>
              <option value="">All clients</option>
              {(clients.data ?? []).map((c) => (
                <option key={c.organization_id} value={c.organization_id}>{c.doctor_name ?? c.organization_id}</option>
              ))}
            </Select>
          </Field>
          <Field label="Find" className="min-w-0 flex-1 sm:flex-none">
            <span className="relative block">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
              <Input className="w-full pl-8 sm:w-48" placeholder="Title or doctor" value={search} onChange={(e) => setSearch(e.target.value)} />
            </span>
          </Field>
          <Field label="Month">
            <Input type="month" className="w-40" value={period} onChange={(e) => e.target.value && setPeriod(e.target.value)} />
          </Field>
          <Button disabled={org === ""} loading={plan.isPending} onClick={() => plan.mutate()} title={org === "" ? "Choose a client first" : undefined}>
            <CalendarPlus className="h-4 w-4" aria-hidden />
            Plan this month
          </Button>
        </div>
      </header>
      {plan.isSuccess ? (
        <Alert tone="success">{plan.data.length === 0 ? "Already planned: nothing new to add." : `Planned ${plan.data.length} items from the package.`}</Alert>
      ) : null}
      {plan.isError ? <QueryError error={plan.error} /> : null}
      {move.isError ? <QueryError error={move.error} /> : null}
      {notice !== null ? <Alert tone={notice.tone}>{notice.text}</Alert> : null}
      {items.data !== undefined ? (
        <ClientSummary
          name={org === "" ? null : (client?.doctor_name ?? "This client")}
          orgId={org}
          items={loaded}
          shown={all.length}
          searching={needle !== ""}
        />
      ) : null}
      {items.isPending ? <Spinner label="Loading…" /> : null}
      {items.isError ? <QueryError error={items.error} /> : null}
      {items.data !== undefined ? (
        <DndContext
          sensors={sensors}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          onDragCancel={() => setDragging(null)}
          accessibility={{
            screenReaderInstructions: { draggable: "To move a card, press space or enter, use the arrow keys to choose a column, then press space or enter again. Press escape to cancel." },
            announcements: {
              onDragStart: ({ active }) => `Picked up ${all.find((d) => d.id === active.id)?.title ?? "card"}.`,
              onDragOver: ({ over }) => (over ? `Over ${LABEL[over.id as Status]}.` : "Not over a column."),
              onDragEnd: ({ over }) => (over ? `Dropped in ${LABEL[over.id as Status]}.` : "Dropped back."),
              onDragCancel: () => "Move cancelled.",
            },
          }}
        >
          <div
            role="region"
            aria-label="Pipeline board"
            className="flex h-[calc(100dvh-15rem)] min-h-[26rem] snap-x snap-mandatory gap-3 overflow-x-auto overscroll-x-contain pb-2 md:h-[calc(100dvh-13rem)] md:snap-none"
          >
            {COLUMNS.map((c) => (
              <Column
                key={c.status}
                status={c.status}
                label={c.label}
                dot={c.dot}
                items={all.filter((d) => d.status === c.status)}
                state={
                  allowed === null || dragging === null
                    ? "idle"
                    : c.status === dragging.status
                      ? "origin"
                      : !allowed.includes(c.status)
                        ? "blocked"
                        : needsPreview(dragging, c.status)
                          ? "needs_link"
                          : "target"
                }
                onMove={tryMove}
                moving={move.isPending ? move.variables.item.id : null}
                panel={panel}
                showDoctor={org === ""}
                onPanel={(id, kind) => setPanel((p) => (p?.id === id && p.kind === kind ? null : { id, kind }))}
                onDone={(published) => {
                  setPanel(null);
                  setNotice(published ? { tone: "success", text: `Marked “${published}” published. The doctor sees the link in Content and approvals.` } : null);
                }}
              />
            ))}
          </div>
          <DragOverlay dropAnimation={{ duration: 180, easing: "cubic-bezier(0.2, 0, 0, 1)" }}>
            {dragging ? <CardBody item={dragging} lifted showDoctor={org === ""} /> : null}
          </DragOverlay>
        </DndContext>
      ) : null}
    </div>
  );
}

/** How a column reacts while a card is dragged. `needs_link`: allowed, but the card needs a preview link first. */
type ColumnState = "idle" | "origin" | "target" | "needs_link" | "blocked";

function Column({ status, label, dot, items, state, onMove, moving, panel, onPanel, onDone, showDoctor }: {
  status: Status;
  label: string;
  dot: string;
  items: Deliverable[];
  state: ColumnState;
  onMove: (item: Deliverable, to: Status) => void;
  moving: string | null;
  panel: Panel | null;
  onPanel: (id: string, kind: Panel["kind"]) => void;
  /** `published`: the title of an item just marked published. */
  onDone: (published?: string) => void;
  showDoctor: boolean;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: status, disabled: state === "blocked" });
  return (
    <section
      ref={setNodeRef}
      aria-label={label}
      data-state={state}
      className={cn(
        "glass flex w-[85vw] max-w-[20rem] shrink-0 snap-center flex-col rounded-[var(--radius-lg)] transition-[opacity,box-shadow] duration-150 sm:w-72",
        state === "blocked" && "opacity-45",
        state === "target" && "ring-2 ring-azure-400/50",
        state === "target" && isOver && "ring-azure-400 shadow-lg shadow-azure-500/20",
        state === "needs_link" && "ring-2 ring-dashed ring-amber-400/70",
      )}
    >
      <h2 className="flex items-center justify-between gap-2 border-b border-border px-3 py-2.5 text-sm font-semibold">
        <span className="flex min-w-0 items-center gap-2">
          <span aria-hidden className={cn("h-2 w-2 shrink-0 rounded-full", dot)} />
          <span className="truncate">{label}</span>
        </span>
        <span className="rounded-full bg-muted px-2 py-0.5 text-xs tabular-nums text-muted-foreground">{items.length}</span>
      </h2>
      <div className="grid flex-1 content-start gap-2 overflow-y-auto overscroll-contain p-2 [scrollbar-width:thin]">
        {state === "needs_link" ? (
          <p className="rounded-md border border-dashed border-amber-400/70 bg-amber-400/10 px-3 py-2 text-center text-xs font-medium">Add a preview link first</p>
        ) : null}
        {items.length === 0 && state !== "needs_link" ? (
          <p className={cn("rounded-md border border-dashed border-border px-3 py-6 text-center text-xs text-muted-foreground", state === "target" && "border-azure-400/60 text-foreground")}>
            {state === "target" ? (status === "published" ? "Drop to publish" : "Drop here") : "No items"}
          </p>
        ) : (
          items.map((d) => (
            <DraggableCard
              key={d.id}
              item={d}
              onMove={onMove}
              moving={moving === d.id}
              panel={panel?.id === d.id ? panel.kind : null}
              onPanel={(kind) => onPanel(d.id, kind)}
              onDone={onDone}
              showDoctor={showDoctor}
            />
          ))
        )}
      </div>
    </section>
  );
}

function DraggableCard({ item, onMove, moving, panel, onPanel, onDone, showDoctor }: {
  item: Deliverable;
  onMove: (item: Deliverable, to: Status) => void;
  moving: boolean;
  panel: Panel["kind"] | null;
  onPanel: (kind: Panel["kind"]) => void;
  onDone: (published?: string) => void;
  showDoctor: boolean;
}) {
  const movable = boardMoves(item).length > 0;
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, isDragging } = useDraggable({ id: item.id, disabled: !movable });
  const qc = useQueryClient();
  return (
    <div ref={setNodeRef} className={cn("transition-opacity", isDragging && "opacity-30")}>
      <CardBody
        item={item}
        moving={moving}
        showDoctor={showDoctor}
        handle={
          movable ? (
            <button
              ref={setActivatorNodeRef}
              type="button"
              aria-label={`Drag ${item.title}`}
              className="-ml-1 grid h-7 w-6 shrink-0 cursor-grab touch-none place-items-center rounded text-muted-foreground hover:bg-muted hover:text-foreground active:cursor-grabbing"
              {...attributes}
              {...listeners}
            >
              <GripVertical className="h-4 w-4" aria-hidden />
            </button>
          ) : null
        }
        actions={
          <CardActions
            item={item}
            panel={panel}
            onPanel={onPanel}
            onMove={(to) => onMove(item, to)}
          />
        }
      />
      {panel === "edit" || panel === "preview" ? (
        <div className="mt-1 rounded-md border border-border bg-card p-3">
          <EditForm
            item={item}
            focusPreview={panel === "preview"}
            onDone={() => {
              onDone();
              void qc.invalidateQueries({ queryKey: ["admin", "pipeline"] });
            }}
          />
        </div>
      ) : null}
      {panel === "publish" ? (
        <div className="mt-1 rounded-md border border-emerald-500/40 bg-card p-3">
          <PublishForm item={item} onDone={() => onDone(item.title)} onCancel={() => onPanel("publish")} />
        </div>
      ) : null}
    </div>
  );
}

function CardBody({ item, handle, actions, lifted = false, moving = false, showDoctor = false }: {
  item: Deliverable;
  handle?: React.ReactNode;
  actions?: React.ReactNode;
  lifted?: boolean;
  moving?: boolean;
  showDoctor?: boolean;
}) {
  return (
    <article
      className={cn(
        "grid gap-2 rounded-md border border-border bg-card p-3 text-sm text-card-foreground shadow-sm",
        lifted && "rotate-1 cursor-grabbing shadow-2xl ring-2 ring-azure-400/60",
        moving && "animate-pulse",
      )}
    >
      {showDoctor ? (
        <p className="flex min-w-0 items-center gap-2 text-xs font-semibold">
          <span aria-hidden className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-azure-500/20 text-[10px] text-azure-300">{initials(item.doctor_name ?? "")}</span>
          <span className="truncate">{item.doctor_name ?? "Unknown practice"}</span>
        </p>
      ) : null}
      <div className="flex items-start gap-1.5">
        {handle}
        <p className="min-w-0 flex-1 font-medium leading-snug [overflow-wrap:anywhere]">{item.title}</p>
      </div>
      <p className="text-xs text-muted-foreground">
        {KIND[item.kind]} · due {item.due_on}
        {item.revision > 1 ? ` · v${item.revision}` : ""}
      </p>
      {item.status === "changes_requested" ? <Badge tone="warning">Changes requested</Badge> : null}
      {item.status === "sent_to_doctor" ? <span className="text-xs text-muted-foreground">Waiting for doctor</span> : null}
      {item.next.includes("sent_to_doctor") && !item.preview_url ? <Badge tone="neutral" className="w-fit">No preview link</Badge> : null}
      {actions}
    </article>
  );
}

/** Touch- and keyboard-friendly alternative to dragging, plus publish, preview and edit. */
function CardActions({ item, panel, onPanel, onMove }: { item: Deliverable; panel: Panel["kind"] | null; onPanel: (kind: Panel["kind"]) => void; onMove: (to: Status) => void }) {
  const { platform } = useBrandex();
  const moves = movesFor(item);
  return (
    <div className="flex flex-wrap items-center gap-1">
      {item.status === "approved" || item.status === "published" ? (
        <Button
          size="sm"
          variant={item.status === "approved" ? "accent" : "outline"}
          className="flex-1"
          aria-expanded={panel === "publish"}
          aria-label={item.status === "approved" ? `Publish ${item.title}` : `Add another channel for ${item.title}`}
          onClick={() => onPanel("publish")}
        >
          <Send className="h-3.5 w-3.5" aria-hidden />
          {item.status === "approved" ? "Publish" : "Add channel"}
        </Button>
      ) : null}
      {moves.length > 0 ? (
        <Select
          aria-label={`Move ${item.title}`}
          className="h-8 min-w-0 flex-1 py-0 text-xs"
          value=""
          onChange={(e) => e.target.value && onMove(e.target.value as Status)}
        >
          <option value="">Move to…</option>
          {moves.map((to) => (
            <option key={to} value={to}>{item.status === "sent_to_doctor" && to === "internal_review" ? "Recall to internal review" : LABEL[to]}</option>
          ))}
        </Select>
      ) : null}
      {item.preview_url ? (
        <Button size="sm" variant="ghost" aria-label={`Open preview of ${item.title}`} onClick={() => void platform.openExternal(item.preview_url ?? "")}>
          <ExternalLink className="h-3.5 w-3.5" aria-hidden />
        </Button>
      ) : null}
      {item.status !== "approved" && item.status !== "published" ? (
        <Button size="sm" variant="ghost" aria-label={`Edit ${item.title}`} aria-expanded={panel === "edit" || panel === "preview"} onClick={() => onPanel("edit")}>
          <Pencil className="h-3.5 w-3.5" aria-hidden />
        </Button>
      ) : null}
    </div>
  );
}

function EditForm({ item, onDone, focusPreview = false }: { item: Deliverable; onDone: () => void; focusPreview?: boolean }) {
  const { api } = useBrandex();
  const [title, setTitle] = useState(item.title);
  const [preview, setPreview] = useState(item.preview_url ?? "");
  const [caption, setCaption] = useState(item.caption);
  const [due, setDue] = useState(item.due_on);
  const save = useMutation({
    mutationFn: async () => {
      await api.http.PUT("/v1/admin/deliverables/{deliverable_id}", {
        params: { path: { deliverable_id: item.id } },
        body: { title, brief: item.brief, caption, preview_url: preview.trim() === "" ? null : preview.trim(), due_on: due },
      });
    },
    onSuccess: onDone,
  });
  return (
    <form className="grid gap-2" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
      <Field label="Title"><Input value={title} maxLength={160} onChange={(e) => setTitle(e.target.value)} /></Field>
      <Field label="Preview link" hint="Where the doctor reviews the cut (required before sending)">
        {/* Focused when a send was blocked for want of a link. */}
        <Input type="url" autoFocus={focusPreview} value={preview} placeholder="https://" onChange={(e) => setPreview(e.target.value)} />
      </Field>
      <Field label="Caption"><Textarea rows={2} maxLength={2200} value={caption} onChange={(e) => setCaption(e.target.value)} /></Field>
      <Field label="Due"><Input type="date" value={due} onChange={(e) => setDue(e.target.value)} /></Field>
      {save.isError ? <QueryError error={save.error} /> : null}
      <div><Button type="submit" size="sm" loading={save.isPending}>Save</Button></div>
    </form>
  );
}

/** Who the board shows, and where their items stand. */
function ClientSummary({ name, orgId, items, shown, searching }: { name: string | null; orgId: string; items: Deliverable[]; shown: number; searching: boolean }) {
  const { nav } = useBrandex();
  const count = (status: Status) => items.filter((d) => d.status === status).length;
  const done = items.filter((d) => d.status === "approved" || d.status === "published").length;
  const stats: [string, number, string][] = [
    ["Waiting for doctor", count("sent_to_doctor"), "text-amber-400"],
    ["Changes requested", count("changes_requested"), "text-coral-400"],
    ["Approved or live", done, "text-emerald-400"],
  ];
  return (
    <div className="glass flex flex-wrap items-center justify-between gap-4 rounded-[var(--radius-lg)] px-4 py-3">
      <div className="min-w-0">
        <p className="flex items-center gap-2 font-semibold">
          {name !== null ? <span aria-hidden className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-azure-500/20 text-xs text-azure-300">{initials(name)}</span> : null}
          <span className="truncate">{name ?? "All clients"}</span>
        </p>
        <p className="text-xs text-muted-foreground">
          {searching ? `${shown} of ${items.length} items match` : `${items.length} items this month`}
          {name === null ? " · choose a client to work on one practice" : ""}
        </p>
      </div>
      <dl aria-label="Where items stand" className="flex flex-wrap gap-x-6 gap-y-1">
        {stats.map(([label, n, tone]) => (
          <div key={label} className="text-sm">
            <dt className="text-xs text-muted-foreground">{label}</dt>
            <dd className={cn("font-display text-lg font-bold tabular-nums", n > 0 ? tone : "text-muted-foreground")}>{n}</dd>
          </div>
        ))}
      </dl>
      {name !== null ? (
        <nav.Link href={`/clients/${orgId}/report`} className="inline-flex items-center gap-1.5 text-sm font-medium text-azure-400 hover:underline">
          <FileBarChart className="h-4 w-4" aria-hidden />
          Client report
        </nav.Link>
      ) : null}
    </div>
  );
}
